import { captureClientDetailSelection, ownsClientDetailSelection } from "store/slices/clientDetailOwnership";
import PropTypes from "prop-types";
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { ClientDetailConsultationFormTab, ClientformApi, ClientformDetailApi, ClientFormProfileSyncApi } from 'store/slices/clientformSlice';
import { ClientDetailApi } from "store/slices/clientSlice";
import config from "../../../../../config";
import AuthenticatedFormSignature from './AuthenticatedFormSignature';
import ProfileSyncReview from '../ProfileSyncReview';
import { profileSyncForSubmission } from '../profileSyncContract';

const clientFormType = (answer) => String(answer?.form_type || "").trim().toLowerCase();

// Current submissions use JSON arrays; migrated submissions may use comma-separated text.
const selectedOptions = (answer, formType) => {
  let value = answer;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) value = parsed;
    } catch {
      // Plain text is the legacy answer format.
    }
  }
  const values = Array.isArray(value) ? value
    : formType === "multicheckbox" ? String(value ?? "").split(",") : [value];
  return values.filter(value => value != null && String(value).trim() !== "").map(String);
};

const ClientFormDetails = ({ canUpdateClient = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const store = useStore();
  const rightDrawerOpened = useSelector((state) => state.clientform.isclientDetailConsultationFormTab);
  const isclientDetailConsultationForm = useSelector((state) => state.clientform.isclientDetailConsultationForm);
  const profileSyncError = useSelector((state) => state.clientform.isProfileSyncError);
  const profileSyncStatus = useSelector((state) => state.clientform.isApiStatus?.ClientFormProfileSyncApi);
  const clientDetail = useSelector((state) => state.client.isDetailData) || {};
  const submission = Array.isArray(isclientDetailConsultationForm)
    ? { answers: isclientDetailConsultationForm }
    : (isclientDetailConsultationForm || {});
  const answers = Array.isArray(submission.answers)
    ? submission.answers
    : Array.isArray(submission.form_answer)
      ? submission.form_answer
      : [];
  const clientId = submission.client_id || clientDetail.id;
  const profileSync = profileSyncForSubmission(submission);
  const handleProfileDecision = async (field, action) => {
    if (!canUpdateClient || !clientId || !submission.id) return;
    const selection = captureClientDetailSelection(store.getState().client);
    if (selection.clientId !== Number(clientId) || !ownsClientDetailSelection(store.getState().client, selection)) return;
    const result = await dispatch(ClientFormProfileSyncApi({
      client_id: clientId,
      id: submission.id,
      decisions: [{ field, action }],
      revision: profileSync.revision,
    }));
    if (result.meta.requestStatus === "fulfilled" && ownsClientDetailSelection(store.getState().client, selection)) {
      dispatch(ClientformApi({ client_id: clientId }));
      dispatch(ClientDetailApi({ id: clientId }));
    }
  };

  const reloadProfileSync = () => {
    const selection = captureClientDetailSelection(store.getState().client);
    if (selection.clientId !== Number(clientId) || !ownsClientDetailSelection(store.getState().client, selection)) return;
    if (clientId && submission.id) {
      dispatch(ClientformDetailApi({ client_id: clientId, id: submission.id }));
    }
  };

  return (
    <div className={"drawer addnote-drawer p-0 " + rightDrawerOpened}>
      <div className="drawer-wrp position-relative">
        <div className="drawer-header">
          <h2 className="mt-3 ms-3 pe-md-5 pe-3">{t("Consultation Forms")}</h2>
          <a href="#" className="close mt-2 me-4" onClick={() => {
            dispatch(ClientDetailConsultationFormTab(""));
          }}>
            <img src={config.imagepath + "close-icon.svg"} alt="" />
          </a>
        </div>
        <div className="drawer-body">
          <div className="row mx-4 mb-5">
            <ProfileSyncReview
              profileSync={submission.profile_sync || submission.profileSync}
              canUpdate={canUpdateClient}
              loading={profileSyncStatus === "loading"}
              error={profileSyncError}
              onDecision={handleProfileDecision}
              onReload={reloadProfileSync}
            />
            {answers.map((formAnswer, i) => {
              const formType = clientFormType(formAnswer);
              const isSignature = ["file", "signature"].includes(formType);
              const answer = formAnswer.answer ?? "";
              void (formAnswer.question ? formAnswer.question : "");
              return (
                <div key={formAnswer.id || i} className='col-12'>
                  <div className="row">
                    {formAnswer.form_element_type && formAnswer.form_element_type.name ? (
                      <div className="col-8 w-100 fw-semibold mb-2 ms-0 ps-0">
                        <h6 className='fw-bold mb-0'>{formAnswer.form_element_type.name}</h6>
                      </div>
                    ) : null}
                    {formAnswer.question ? (
                      <div className="col-8 w-100 fw-semibold mb-2 ps-0">
                        {formAnswer.question}
                      </div>
                    ) : null}
                    {formAnswer.answer ? (
                      <>
                        <div className="mb-2 col-8 form-preview-width-control w-100">
                          {["multicheckbox", "checkbox", "radio"].includes(formType) ? selectedOptions(answer, formType).map((option, index) => (
                            <label className="mb-2 d-block" key={`${option}-${index}`}>
                              <input type={formType === "radio" ? "radio" : "checkbox"} className="form-check-input me-2" checked disabled readOnly />
                              <span className="fw-semibold">{option}</span>
                            </label>
                          )) : null}
                        </div>
                        {answer && !isSignature ? (
                          <div className={`mb-2 col-8 w-100 form-preview-width-control ${formType === "text" || formType === "date" ? "form-control" : ""} ${formType === "select" ? "formpreview-select-form" : ""} ${formType === "textarea" ? "formpreview-textarea-form" : ""}`}>
                            {["multicheckbox", "checkbox", "radio"].includes(formType) ? null : answer}
                          </div>
                        ) : null}
                        {isSignature ? (
                          <div className="signature-pad-wrapper">
                            <AuthenticatedFormSignature clientId={clientId} submissionId={submission.id} answerId={formAnswer.id} />
                          </div>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

ClientFormDetails.propTypes = {
  canUpdateClient: PropTypes.bool,
};

export default ClientFormDetails;
